import React, { useState, useMemo } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import "../../assets/css/Daywisetable.css";

export const DAYS = Array.from({ length: 16 }, (_, i) => `D${i}`);

const toNum = (v) => {
	const n = parseFloat(v);
	return Number.isFinite(n) ? n : 0;
};

const buildDayMap = (data) => {
	const map = {};
	(data || []).forEach((d) => {
		map[d.day] = d;
	});
	return map;
};

const CustomTooltip = ({ active, payload, label, unit = "" }) => {
	if (!active || !payload?.length) return null;
	return (
		<div style={styles.tooltip}>
			<p style={styles.tooltipTitle}>{label}</p>
			{payload.map((p, i) => (
				<p key={i} style={{ ...styles.tooltipRow, color: p.color }}>
					{p.name}: <strong>{p.value}{unit}</strong>
				</p>
			))}
		</div>
	);
};

const DayDetailPanel = ({ title, color, detail }) => {
	const categories = detail?.categories ?? [];
	const topOffers = detail?.topOffers ?? [];

	if (!categories.length && !topOffers.length) {
		return (
			<div style={{ ...styles.panel, borderColor: (color || "#E5E7EB") + "40" }}>
				{title && <p style={{ ...styles.panelTitle, color }}>{title}</p>}
				<p style={styles.noData}>No offer activity for this day.</p>
			</div>
		);
	}

	return (
		<div style={{ ...styles.panel, borderColor: (color || "#E5E7EB") + "40" }}>
			{title && <p style={{ ...styles.panelTitle, color }}>{title}</p>}

			{categories.length > 0 && (
				<div className="dwt-section">
					<h4 className="dwt-section-title">📦 Category Breakdown</h4>
					<div className="dwt-scroll">
						<table className="dwt-table">
							<thead className="dwt-thead">
								<tr>
									<th className="dwt-th">Category</th>
									<th className="dwt-th">Clicks</th>
									<th className="dwt-th">Completed</th>
									<th className="dwt-th">Payout</th>
								</tr>
							</thead>
							<tbody>
								{categories.map((cat, i) => (
									<tr key={cat.category ?? i} className={i % 2 === 0 ? "dwt-row-even" : "dwt-row-odd"}>
										<td className="dwt-td">
											<span className="dwt-day-label">{cat.category}</span>
										</td>
										<td className="dwt-td">{(cat.total_clicks || 0).toLocaleString()}</td>
										<td className="dwt-td">{(cat.completed_clicks || 0).toLocaleString()}</td>
										<td className="dwt-td">
											<span className="dwt-payout">${((cat.total_payout || 0) / 1000).toFixed(2)}</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{topOffers.length > 0 && (
				<div className="dwt-section" style={{ marginBottom: 0 }}>
					<h4 className="dwt-section-title">🏆 Top Offers</h4>
					<div className="dwt-scroll">
						<table className="dwt-table">
							<thead className="dwt-thead">
								<tr>
									<th className="dwt-th">#</th>
									<th className="dwt-th">Offer</th>
									<th className="dwt-th">Users</th>
									<th className="dwt-th">Clicks</th>
									<th className="dwt-th">Completed</th>
									<th className="dwt-th">Payout</th>
								</tr>
							</thead>
							<tbody>
								{topOffers.map((offer, i) => (
									<tr key={offer.offerId ?? offer._id ?? i} className={i % 2 === 0 ? "dwt-row-even" : "dwt-row-odd"}>
										<td className="dwt-td">
											<span className="dwt-top-offer-rank">{i + 1}</span>
										</td>
										<td className="dwt-td">
											<span className="dwt-day-label">{offer.offer_name || "—"}</span>
										</td>
										<td className="dwt-td">{(offer.total_users || 0).toLocaleString()}</td>
										<td className="dwt-td">{(offer.total_clicks || 0).toLocaleString()}</td>
										<td className="dwt-td">{(offer.completed_clicks || 0).toLocaleString()}</td>
										<td className="dwt-td">
											<span className="dwt-payout">${((offer.total_payout || 0) / 1000).toFixed(2)}</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	);
};

/**
 * seriesA / seriesB: { label, color, data: [{ day, avg_clicks_per_user, avg_completed_per_user }] }
 * getDetailA / getDetailB: (day) => { categories, topOffers }
 */
const OffersEngagementChart = ({ seriesA, seriesB, getDetailA, getDetailB }) => {
	const [selectedDay, setSelectedDay] = useState("D0");

	const mapA = useMemo(() => buildDayMap(seriesA?.data), [seriesA]);
	const mapB = useMemo(() => buildDayMap(seriesB?.data), [seriesB]);

	const keyAClicks = seriesA ? `${seriesA.label} · Clicks/User` : null;
	const keyBClicks = seriesB ? `${seriesB.label} · Clicks/User` : null;

	// Raw avg-per-user counts aren't directly comparable across ranges with
	// different user volumes/scale, so completion RATE (%) is plotted
	// separately — normalised and directly comparable group-over-group.
	const keyACompletion = seriesA ? `${seriesA.label} · Completion %` : null;
	const keyBCompletion = seriesB ? `${seriesB.label} · Completion %` : null;

	const completionRate = (row) => {
		const clicks = toNum(row?.avg_clicks_per_user);
		const completed = toNum(row?.avg_completed_per_user);
		return clicks > 0 ? +((completed / clicks) * 100).toFixed(1) : 0;
	};

	const clicksChartData = useMemo(
		() =>
			DAYS.map((day) => {
				const row = { day };
				if (seriesA) row[keyAClicks] = toNum(mapA[day]?.avg_clicks_per_user);
				if (seriesB) row[keyBClicks] = toNum(mapB[day]?.avg_clicks_per_user);
				return row;
			}),
		[mapA, mapB, seriesA, seriesB, keyAClicks, keyBClicks]
	);

	const completionChartData = useMemo(
		() =>
			DAYS.map((day) => {
				const row = { day };
				if (seriesA) row[keyACompletion] = completionRate(mapA[day]);
				if (seriesB) row[keyBCompletion] = completionRate(mapB[day]);
				return row;
			}),
		[mapA, mapB, seriesA, seriesB, keyACompletion, keyBCompletion]
	);

	const handleChartClick = (e) => {
		if (e?.activeLabel) setSelectedDay(e.activeLabel);
	};

	const detailA = getDetailA ? getDetailA(selectedDay) : null;
	const detailB = getDetailB ? getDetailB(selectedDay) : null;

	return (
		<div>
			<p style={styles.hint}>
				Tap/click a point on either chart — or use the dropdown — to drill into that day's offer performance.
			</p>

			<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 20 }}>
				<div>
					<p style={styles.chartLabel}>📊 Avg Clicks / User</p>
					<ResponsiveContainer width="100%" height={260}>
						<LineChart data={clicksChartData} onClick={handleChartClick} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
							<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
							<XAxis dataKey="day" tick={{ fontSize: 12 }} />
							<YAxis tick={{ fontSize: 12 }} />
							<Tooltip content={(props) => <CustomTooltip {...props} />} />
							<Legend wrapperStyle={{ fontSize: 12 }} />
							{seriesA && (
								<Line
									type="monotone"
									dataKey={keyAClicks}
									stroke={seriesA.color}
									strokeWidth={2}
									dot={{ r: 3, cursor: "pointer" }}
									activeDot={{ r: 6, cursor: "pointer" }}
								/>
							)}
							{seriesB && (
								<Line
									type="monotone"
									dataKey={keyBClicks}
									stroke={seriesB.color}
									strokeWidth={2}
									dot={{ r: 3, cursor: "pointer" }}
									activeDot={{ r: 6, cursor: "pointer" }}
								/>
							)}
						</LineChart>
					</ResponsiveContainer>
				</div>

				<div>
					<p style={styles.chartLabel}>✅ Completion Rate (%)</p>
					<p style={styles.chartSubLabel}>Completed ÷ total clicks per day — normalised, directly comparable across ranges.</p>
					<ResponsiveContainer width="100%" height={260}>
						<LineChart data={completionChartData} onClick={handleChartClick} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
							<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
							<XAxis dataKey="day" tick={{ fontSize: 12 }} />
							<YAxis unit="%" tick={{ fontSize: 12 }} />
							<Tooltip content={(props) => <CustomTooltip {...props} unit="%" />} />
							<Legend wrapperStyle={{ fontSize: 12 }} />
							{seriesA && (
								<Line
									type="monotone"
									dataKey={keyACompletion}
									stroke={seriesA.color}
									strokeWidth={2}
									dot={{ r: 3, cursor: "pointer" }}
									activeDot={{ r: 6, cursor: "pointer" }}
								/>
							)}
							{seriesB && (
								<Line
									type="monotone"
									dataKey={keyBCompletion}
									stroke={seriesB.color}
									strokeWidth={2}
									dot={{ r: 3, cursor: "pointer" }}
									activeDot={{ r: 6, cursor: "pointer" }}
								/>
							)}
						</LineChart>
					</ResponsiveContainer>
				</div>
			</div>

			<div style={styles.daySelectRow}>
				<span style={styles.daySelectLabel}>Selected day:</span>
				<select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)} style={styles.daySelect}>
					{DAYS.map((d) => (
						<option key={d} value={d}>
							{d}
						</option>
					))}
				</select>
			</div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: detailA && detailB ? "repeat(auto-fit, minmax(340px, 1fr))" : "1fr",
					gap: 20,
					marginTop: 16,
				}}
			>
				{detailA && <DayDetailPanel title={seriesA?.label} color={seriesA?.color} detail={detailA} />}
				{detailB && <DayDetailPanel title={seriesB?.label} color={seriesB?.color} detail={detailB} />}
			</div>
		</div>
	);
};

const styles = {
	hint: {
		fontSize: 13,
		color: "var(--text-muted)",
		marginTop: 0,
		marginBottom: 12,
	},
	chartLabel: {
		fontSize: 13,
		fontWeight: 700,
		color: "var(--text-primary)",
		margin: "0 0 4px 0",
	},
	chartSubLabel: {
		fontSize: 12,
		color: "var(--text-muted)",
		margin: "0 0 8px 0",
	},
	tooltip: {
		background: "#fff",
		border: "1px solid #E5E7EB",
		borderRadius: 8,
		padding: "10px 14px",
		fontSize: 13,
		boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
	},
	tooltipTitle: { fontWeight: 600, marginBottom: 6, marginTop: 0, color: "#111" },
	tooltipRow: { margin: "3px 0" },
	daySelectRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginTop: 12,
		flexWrap: "wrap",
	},
	daySelectLabel: { fontSize: 13, color: "var(--text-muted)", fontWeight: 500 },
	daySelect: {
		padding: "6px 10px",
		border: "1px solid var(--border-color, #D1D5DB)",
		borderRadius: 6,
		fontSize: 13,
		background: "var(--bg-card, #fff)",
		color: "var(--text-primary)",
		cursor: "pointer",
		outline: "none",
	},
	panel: {
		border: "1px solid #E5E7EB",
		borderRadius: 10,
		padding: "16px",
		background: "var(--bg-card, #fff)",
		minWidth: 0,
	},
	panelTitle: {
		margin: "0 0 12px 0",
		fontSize: 14,
		fontWeight: 700,
	},
	noData: {
		textAlign: "center",
		color: "var(--text-muted)",
		padding: "24px 0",
		margin: 0,
	},
};

export default OffersEngagementChart;
